import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Palette, TrendingUp, Clock, Filter, Search, Star, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const API = '/api';

// Helper to resolve image URLs (handles both relative and absolute URLs)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (url.startsWith('/')) {
    return url;
  }
  return `/${url}`;
};

const CATEGORIES = [
  'All',
  'Character Design',
  'Concept Art',
  'Fan Art',
  'Illustration',
  'Comic Art',
  'Cover Art',
  'Sketches',
  'Digital Painting',
  'Traditional Art'
];

// Memory-optimized artwork card component
const ArtworkCard = React.memo(({ artwork, isFeatured, isAdmin, onFeatureToggle, isToggling }) => {
  // Priority: thumb_url (optimized 400px) > thumbnail_url > image_url > first image
  // NEVER use full_url in list views
  const rawImageUrl = artwork.thumb_url || artwork.thumbnail_url || artwork.image_url || artwork.images?.[0]?.url;
  const imageUrl = resolveImageUrl(rawImageUrl);
  const [error, setError] = useState(false);

  return (
    <div className="relative mb-2 sm:mb-3 break-inside-avoid group">
      {/* Featured Badge - Top Left */}
      {isFeatured && (
        <div className="absolute top-2 left-2 z-20">
          <Badge 
            className="bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs px-2 py-0.5 shadow-lg"
            data-testid={`featured-badge-${artwork.id}`}
          >
            <Sparkles className="w-3 h-3 mr-1" />
            Featured
          </Badge>
        </div>
      )}
      
      {/* Admin Feature Button - Top Right */}
      {isAdmin && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onFeatureToggle(artwork.id, artwork.title);
          }}
          disabled={isToggling}
          className={`absolute top-2 right-2 z-20 p-1.5 rounded-full transition-all duration-200 ${
            isToggling 
              ? 'bg-gray-700 cursor-wait' 
              : isFeatured 
                ? 'bg-yellow-500 hover:bg-yellow-600 text-black' 
                : 'bg-black/60 hover:bg-black/80 text-white hover:text-yellow-400'
          }`}
          title={isFeatured ? 'Remove from featured' : 'Feature this artwork'}
          data-testid={`feature-btn-${artwork.id}`}
        >
          {isToggling ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Star className={`w-4 h-4 ${isFeatured ? 'fill-current' : ''}`} />
          )}
        </button>
      )}
      
      <Link 
        to={`/art/${artwork.id}`}
        className="block"
        data-testid={`artwork-card-${artwork.id}`}
      >
        <div className="relative rounded-lg overflow-hidden bg-gray-900">
          {/* Artwork Image with native lazy loading */}
          {imageUrl && !error ? (
            <img
              src={imageUrl}
              alt={artwork.title || 'Artwork'}
              width={300}
              height={300}
              className="w-full h-auto object-cover group-hover:scale-[1.02] transition-transform duration-300"
              loading="lazy"
              decoding="async"
              onError={() => setError(true)}
            />
          ) : (
            <div className="w-full aspect-square flex items-center justify-center text-xs text-gray-600 bg-gray-900">
              <Palette className="w-8 h-8 opacity-30" />
            </div>
          )}
          
          {/* Hover Overlay - Desktop only */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none hidden sm:flex flex-col justify-end p-3">
            <h3 className="text-white font-semibold text-sm line-clamp-1 mb-1">
              {artwork.title || 'Untitled'}
            </h3>
            <p className="text-gray-300 text-xs line-clamp-1">
              {artwork.artist_name || 'Unknown Artist'}
            </p>
          </div>
        </div>
      </Link>
    </div>
  );
});

ArtworkCard.displayName = 'ArtworkCard';

const ArtGallery = () => {
  useDocumentTitle('Gallery');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.is_admin;
  
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  
  // Featured artwork state (admin only)
  const [featuredIds, setFeaturedIds] = useState(new Set());
  const [featuringId, setFeaturingId] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  
  // Intersection observer for infinite scroll
  const observerTarget = useRef(null);

  // Fetch featured artwork IDs
  const fetchFeaturedIds = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/featured/artworks/ids`);
      setFeaturedIds(new Set(response.data.featured_ids || []));
    } catch (error) {
      console.error('Error fetching featured IDs:', error);
    }
  }, []);

  const fetchArtworks = useCallback(async (reset = false) => {
    if (loading && !reset) return;
    if (loadingMore) return;
    
    try {
      if (reset) {
        setLoading(true);
        setArtworks([]);
        setNextCursor(null);
      } else {
        setLoadingMore(true);
      }
      
      const params = {
        limit: 24,
        category,
        sort: sortBy,
      };
      
      if (!reset && nextCursor) {
        params.cursor = nextCursor;
      }
      
      const response = await axios.get(`${API}/art/gallery`, { params });

      const newPosts = response.data.posts || response.data.items || [];
      
      if (reset) {
        setArtworks(newPosts);
      } else {
        setArtworks(prev => [...prev, ...newPosts]);
      }
      
      setHasMore(response.data.has_more || false);
      setNextCursor(response.data.next_cursor || null);
    } catch (error) {
      console.error('Error fetching artworks:', error);
      if (reset) setArtworks([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [category, sortBy, nextCursor, loading, loadingMore]);

  // Initial load and filter changes
  useEffect(() => {
    fetchArtworks(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, sortBy]);

  // Fetch featured IDs once on mount
  useEffect(() => {
    fetchFeaturedIds();
  }, [fetchFeaturedIds]);

  // Infinite scroll observer
  useEffect(() => {
    if (!observerTarget.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          fetchArtworks(false);
        }
      },
      { rootMargin: '200px' }
    );
    
    observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, fetchArtworks]);

  // Handle featuring/unfeaturing artwork
  const handleFeatureToggle = async (artworkId, artworkTitle) => {
    const isFeatured = featuredIds.has(artworkId);
    
    if (isFeatured) {
      setConfirmRemove({ id: artworkId, title: artworkTitle });
      return;
    }
    
    await addToFeatured(artworkId, artworkTitle);
  };

  const addToFeatured = async (artworkId, artworkTitle) => {
    setFeaturingId(artworkId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/featured/artworks/add`,
        { art_post_id: artworkId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setFeaturedIds(prev => new Set([...prev, artworkId]));
      toast.success(`"${artworkTitle || 'Artwork'}" added to Featured`);
    } catch (error) {
      console.error('Error adding to featured:', error);
      toast.error(error.response?.data?.detail || 'Failed to add to featured');
    } finally {
      setFeaturingId(null);
    }
  };

  const removeFromFeatured = async () => {
    if (!confirmRemove) return;
    
    const { id: artworkId, title: artworkTitle } = confirmRemove;
    setFeaturingId(artworkId);
    setConfirmRemove(null);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/featured/artworks/remove`,
        { art_post_id: artworkId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setFeaturedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(artworkId);
        return newSet;
      });
      toast.success(`"${artworkTitle || 'Artwork'}" removed from Featured`);
    } catch (error) {
      console.error('Error removing from featured:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove from featured');
    } finally {
      setFeaturingId(null);
    }
  };

  // Client-side search filter
  const filteredArtworks = searchTerm
    ? artworks.filter(art => 
        art.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        art.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        art.artist_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : artworks;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Minimal Header */}
      <div className="bg-gradient-to-b from-purple-900/30 to-black border-b border-gray-800/50">
        <div className="container mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Title */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold">Gallery</h1>
              {isAdmin && (
                <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                  Admin Mode
                </Badge>
              )}
            </div>
            
            {/* Search Bar */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search artwork..."
                  className="w-full bg-gray-900/80 border border-gray-800 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
                />
              </div>
            </div>

            {/* Sort Buttons */}
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setSortBy('recent')}
                variant="ghost"
                size="sm"
                className={`${sortBy === 'recent' ? 'bg-purple-500/20 text-purple-400' : 'text-gray-400 hover:text-white'}`}
              >
                <Clock className="w-4 h-4 mr-1" />
                Recent
              </Button>
              <Button
                onClick={() => setSortBy('popular')}
                variant="ghost"
                size="sm"
                className={`${sortBy === 'popular' ? 'bg-purple-500/20 text-purple-400' : 'text-gray-400 hover:text-white'}`}
              >
                <TrendingUp className="w-4 h-4 mr-1" />
                Popular
              </Button>
            </div>
          </div>

          {/* Category Pills - Compact */}
          <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  category === cat
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50 hover:text-white'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="container mx-auto px-2 sm:px-4 py-4">
        {/* Initial Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-purple-500 border-t-transparent mx-auto mb-3"></div>
              <p className="text-gray-500 text-sm">Loading...</p>
            </div>
          </div>
        )}

        {/* Masonry Grid - Memory Optimized */}
        {!loading && filteredArtworks.length > 0 && (
          <>
            <div className="columns-2 sm:columns-3 lg:columns-4 xl:columns-5 2xl:columns-6 gap-2 sm:gap-3">
              {filteredArtworks.map((artwork) => (
                <ArtworkCard
                  key={artwork.id}
                  artwork={artwork}
                  isFeatured={featuredIds.has(artwork.id)}
                  isAdmin={isAdmin}
                  onFeatureToggle={handleFeatureToggle}
                  isToggling={featuringId === artwork.id}
                />
              ))}
            </div>

            {/* Infinite scroll observer target */}
            <div ref={observerTarget} className="h-10 flex items-center justify-center">
              {loadingMore && (
                <div className="flex items-center gap-2 text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Loading more...</span>
                </div>
              )}
            </div>
            
            {/* End of content indicator */}
            {!hasMore && artworks.length > 0 && (
              <div className="text-center py-8 text-gray-500 text-sm">
                You&apos;ve reached the end
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!loading && filteredArtworks.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Palette className="w-16 h-16 text-gray-700 mb-4" />
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No artwork found</h3>
            <p className="text-gray-500 text-sm max-w-md">
              {searchTerm 
                ? `No results for "${searchTerm}". Try a different search term.`
                : 'Be the first to share your art with the community!'
              }
            </p>
            {searchTerm && (
              <Button
                onClick={() => setSearchTerm('')}
                variant="ghost"
                className="mt-4 text-purple-400 hover:text-purple-300"
              >
                Clear search
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Remove from Featured Confirmation Modal */}
      <Dialog open={!!confirmRemove} onOpenChange={(open) => !open && setConfirmRemove(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-sm" data-testid="remove-featured-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              Remove from Featured?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-400 text-sm">
              Are you sure you want to remove <span className="text-white font-medium">&quot;{confirmRemove?.title || 'this artwork'}&quot;</span> from the Featured section?
            </p>
            <p className="text-zinc-500 text-xs mt-2">
              This artwork will no longer appear in the Featured Artworks carousel on the homepage.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="border-zinc-700"
              onClick={() => setConfirmRemove(null)}
              data-testid="cancel-remove-btn"
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={removeFromFeatured}
              data-testid="confirm-remove-btn"
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArtGallery;
